import React, { useEffect, useMemo, useState } from 'react';
import {
  ReloadOutlined,
  RightOutlined,
} from '@ant-design/icons';

import { resolveSkillIcon } from './capabilityIcons.jsx';
import SkillDetailDrawer from './SkillDetailDrawer.jsx';

const errorText = (error) => String(error?.message || error || '').trim();
const skillStatusText = (item) => [
  item.registered === false ? '未安装' : '已安装',
  item.enabled ? '自动调用' : '已停用',
].join(' · ');

function SkillRow({ item, onOpenDetails, onToggle, updating }) {
  const open = () => onOpenDetails(item.target_id);
  return (
    <div
      className="flare-skill-management-row"
      onClick={open}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') open();
      }}
      role="button"
      tabIndex={0}
    >
      <span className="flare-skill-management-icon">{resolveSkillIcon(item)}</span>
      <span className="flare-skill-management-copy">
        <span className="flare-skill-management-name">{item.label || item.target_id}</span>
        <span className="flare-skill-management-meta">{skillStatusText(item)}</span>
      </span>
      <span className="flare-skill-ownership">技能</span>
      <button
        aria-label={`${item.label || item.target_id}${item.enabled ? '已启用' : '已停用'}`}
        aria-checked={item.enabled === true}
        className={`flare-skill-toggle${item.enabled ? ' is-enabled' : ''}`}
        disabled={!item.manageable || updating}
        onClick={(event) => {
          event.stopPropagation();
          void onToggle?.(item)?.catch?.(() => {});
        }}
        role="switch"
        type="button"
      >
        <span />
      </button>
      <RightOutlined className="flare-skill-management-chevron" />
    </div>
  );
}

function SkillPanelFeedback({ loading, error, skills, refresh }) {
  return (
    <>
      {loading && skills.length === 0 ? (
        <div aria-busy="true" className="flare-skill-management-state">加载中…</div>
      ) : null}
      {error ? (
        <div className="flare-skill-management-error" role="alert">
          <span>{errorText(error) || '加载失败'}</span>
          <button aria-label="重试" className="flare-skill-icon-button" onClick={() => void refresh?.(true).catch?.(() => {})} type="button">
            <ReloadOutlined />
          </button>
        </div>
      ) : null}
      {!loading && !error && skills.length === 0 ? (
        <div className="flare-skill-management-state">暂无已安装的技能</div>
      ) : null}
    </>
  );
}

function SkillPanelList({ skills, updatingSkillId, toggle, setSelectedSkillId, viewMode }) {
  return (
    <div className={`flare-skill-management-list is-${viewMode}`}>
      {skills.map((item) => (
        <SkillRow
          item={item}
          key={item.target_id}
          onOpenDetails={setSelectedSkillId}
          onToggle={toggle}
          updating={updatingSkillId === item.target_id}
        />
      ))}
    </div>
  );
}


export default function SkillManagementPanel({ skillManagement = {}, viewMode = 'list' }) {
  const {
    error = null,
    loading = false,
    projection = {},
    refresh,
    toggle,
    updatingSkillId = '',
  } = skillManagement;
  const skills = useMemo(
    () => (Array.isArray(projection.skills) ? projection.skills : []),
    [projection.skills],
  );
  const [selectedSkillId, setSelectedSkillId] = useState('');
  const selectedSkill = useMemo(
    () => skills.find((item) => item.target_id === selectedSkillId) || null,
    [selectedSkillId, skills],
  );
  useEffect(() => {
    if (selectedSkillId && !selectedSkill) setSelectedSkillId('');
  }, [selectedSkill, selectedSkillId]);
  return (
    <section className="flare-skill-management">
      <header className="flare-skill-management-header">
        <div>
          <h2>已安装的技能</h2>
        </div>
        <div className="flare-skill-management-controls">
          <span className="flare-skill-management-count">{skills.length} 个技能</span>
        </div>
      </header>
      <SkillPanelFeedback error={error} loading={loading} refresh={refresh} skills={skills} />
      <SkillPanelList
        setSelectedSkillId={setSelectedSkillId}
        skills={skills}
        toggle={toggle}
        updatingSkillId={updatingSkillId}
        viewMode={viewMode}
      />
      <SkillDetailDrawer
        onClose={() => setSelectedSkillId('')}
        open={Boolean(selectedSkill)}
        projection={projection}
        skill={selectedSkill}
      />
    </section>
  );
}
